import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useFetcherWithAbort } from 'shared/hooks/use-fetcher-with-abort'
import { LECTURE_API } from '../../curriculum/api/lecture-api'
import { LectureId } from '../../curriculum/types/lecture-interface'
import {
  CreateModuleInterface,
  ModuleId,
  ModuleInterface,
} from '../../curriculum/types/module-interface'
import { CourseEditApiInterface, CourseId } from '../../types/course-interface'
import {
  CREATE_LIGHT_COURSE_API,
  getLightCourseCreateLecturePath,
  getLightCourseCreateModulePath,
  getLightCourseDeleteLecturePath,
  getLightCoursePatchOptionsPath,
  getLightCourseUpdateLecturePath,
  getLightCourseUpdateModulePath,
} from '../light-course-api'
import {
  getDefaultLightCourseContent,
  getDefaultLightCourseLectureContent,
} from '../light-course-default-content'
import {
  CreateLightCourseInterface,
  CreateLightCourseLectureInterface,
  CreateLightCourseModuleInterface,
  CreateLightCourseResponseInterface,
  LightCourseCurriculumInterface,
  LightCourseLectureInterface,
  PatchLightCourseOptionsInterface,
  UpdateLightCourseLectureInterface,
} from '../light-course-interface'

export const useLightCourseActions = () => {
  const { fetcher: createLightCourseFetcher } = useFetch<
    RequestMethodsEnum.post,
    CreateLightCourseResponseInterface
  >({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: patchLightCourseOptionsFetcher } = useFetcherWithAbort<
    RequestMethodsEnum.patch,
    CourseEditApiInterface
  >({
    method: RequestMethodsEnum.patch,
  })

  const { fetcher: createLightCourseModuleFetcher } = useFetch<
    RequestMethodsEnum.post,
    ModuleInterface
  >({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: createLightCourseLectureFetcher } = useFetch<
    RequestMethodsEnum.post,
    LightCourseLectureInterface
  >({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: deleteLightCourseLectureFetcher } = useFetch<RequestMethodsEnum.delete>({
    method: RequestMethodsEnum.delete,
  })

  const { fetcher: activateLightCourseLectureFetcher } = useFetch<RequestMethodsEnum.post>({
    method: RequestMethodsEnum.post,
  })

  const { fetcher: updateLightCourseLectureFetcher } = useFetch<
    RequestMethodsEnum.put,
    LightCourseLectureInterface
  >({
    method: RequestMethodsEnum.put,
  })

  const { fetcher: patchLightCourseLectureFetcher } = useFetcherWithAbort<
    RequestMethodsEnum.patch,
    LightCourseLectureInterface
  >({
    method: RequestMethodsEnum.patch,
  })

  const { fetcher: putLightCourseModuleFetcher } = useFetcherWithAbort<
    RequestMethodsEnum.put,
    LightCourseCurriculumInterface['modules'][number]
  >({
    method: RequestMethodsEnum.put,
  })

  const updateLightCourseModule = async (moduleId: ModuleId, data: CreateModuleInterface) => {
    return await putLightCourseModuleFetcher(getLightCourseUpdateModulePath(moduleId), data)
  }

  const createLightCourse = (data: CreateLightCourseInterface) =>
    createLightCourseFetcher(CREATE_LIGHT_COURSE_API, data)

  const createLightCourseWithDefaultValues = async (domainId: number) => {
    return await createLightCourse({
      name: getDefaultLightCourseContent().name,
      description: getDefaultLightCourseContent().description,
      domainId,
    })
  }

  const patchLightCourseOptions = async (
    courseId: number,
    data: PatchLightCourseOptionsInterface,
    requestKey: string = 'lightCourseOptions',
  ) => {
    return await patchLightCourseOptionsFetcher(
      getLightCoursePatchOptionsPath(courseId),
      data,
      undefined,
      requestKey,
    )
  }

  const createLightCourseModule = async (
    courseId: CourseId,
    data: CreateLightCourseModuleInterface,
  ) => {
    return await createLightCourseModuleFetcher(getLightCourseCreateModulePath(courseId), data)
  }

  const createLightCourseLecture = async (
    moduleId: number,
    data: CreateLightCourseLectureInterface,
  ) => {
    return await createLightCourseLectureFetcher(getLightCourseCreateLecturePath(moduleId), data)
  }

  const createLightCourseLectureWithDefaultValues = async (moduleId: number) => {
    return await createLightCourseLecture(moduleId, getDefaultLightCourseLectureContent())
  }

  const deleteLightCourseLecture = async (lectureId: number) => {
    await deleteLightCourseLectureFetcher(getLightCourseDeleteLecturePath(lectureId))
  }

  const activateLightCourseLecture = (update: 'deactivate' | 'activate', id: LectureId) =>
    activateLightCourseLectureFetcher(`${LECTURE_API}/${id}/${update}`)

  const updateLightCourseLecture = (id: LectureId, lecture: UpdateLightCourseLectureInterface) =>
    updateLightCourseLectureFetcher(getLightCourseUpdateLecturePath(id), lecture)

  const patchLightCourseLecture = async (
    id: LectureId,
    lecture: Partial<UpdateLightCourseLectureInterface>,
    requestKey: string = 'patchLightCourseLecture',
  ) => {
    return await patchLightCourseLectureFetcher(
      getLightCourseUpdateLecturePath(id),
      lecture,
      undefined,
      requestKey,
    )
  }

  return {
    createLightCourse,
    createLightCourseWithDefaultValues,
    patchLightCourseOptions,
    createLightCourseModule,
    updateLightCourseModule,

    createLightCourseLecture,
    createLightCourseLectureWithDefaultValues,
    deleteLightCourseLecture,
    activateLightCourseLecture,
    updateLightCourseLecture,
    patchLightCourseLecture,
  }
}
